import { getCookie, deleteCookie, buildRequest } from "./auth.js";
import UnauthorisedError from '../errors/UnauthorisedError.js';

export async function fetchManagedClinics(page, pageSize) {
    let url = `/Optician/Clinic/GetManagedClinics/${encodeURIComponent(pageSize)}/${encodeURIComponent(page)}`;

    try {
        const response = await buildRequest(url);

        if (!response.ok) {
            if (response.status === 404) {
                console.warn('No Managed Clinics found.');
                return [];
            }
            else if (response.status === 401) {
                deleteCookie();
                throw new UnauthorisedError();
            }
            else {
                throw new Error(`Failed to fetch Managed Clinics: ${response.status} ${response.statusText}`);
            }
        }

        return await response.json();
    } catch (error) {
        throw new Error(error.message);
    }
}

export async function fetchEyewearTypes(userId) {
    let url = `/Optician/Clinic/GetManagedClinicEyeWearTypeList/${encodeURIComponent(userId)}`;

    try {
        const response = await buildRequest(url);

        if (!response.ok) {
            if (response.status === 404) {
                console.warn('No Eyewear Types found for the given criteria.');
                return [];
            }
            else if (response.status === 401) {
                deleteCookie();
                throw new UnauthorisedError();
            }
            else {
                throw new Error(`Failed to fetch Eyewear Types: ${response.status} ${response.statusText}`);
            }
        }

        return await response.json();
    } catch (error) {
        throw new Error(error.message);
    }
}

export async function fetchEmployeeRequirements(userId, eyewearType) {
    let url = `/Optician/Clinic/GetManagedClinicEmployeeRequirementsList/${encodeURIComponent(userId)}?EyewearType=${encodeURIComponent(eyewearType)}`;

    try {
        const response = await buildRequest(url);

        if (!response.ok) {
            if (response.status === 404) {
                console.warn('No Employee Requirements found for the given criteria.');
                return [];
            }
            else if (response.status === 401) {
                deleteCookie();
                throw new UnauthorisedError();
            }
            else {
                throw new Error(`Failed to fetch Employee Requirements: ${response.status} ${response.statusText}`);
            }
        }

        return await response.json();
    } catch (error) {
        throw new Error(error.message);
    }
}

export async function fetchApprovedFrames(userId, eyewearType, iPageSize = 200, intPageNumber = 1) {
    let url = `/Optician/Clinic/GetManagedClinicFramesList/${encodeURIComponent(iPageSize)}/${encodeURIComponent(intPageNumber)}/${encodeURIComponent(eyewearType)}/${encodeURIComponent(userId)}`;

    try {
        const response = await buildRequest(url);

        if (!response.ok) {
            if (response.status === 404) {
                console.warn('No Frames found for the given criteria.');
                return [];
            }
            else if (response.status === 401) {
                deleteCookie();
                throw new UnauthorisedError();
            }
            else {
                throw new Error(`Failed to fetch Frames: ${response.status} ${response.statusText}`);
            }
        }

        return await response.json();
    } catch (error) {
        throw new Error(error.message);
    }
}

export async function fetchLensDropDownSelections(userId, frameId, noOverride = false) {
    let url = `/Optician/Clinic/GetManagedClinicLensDropDownSelection/${encodeURIComponent(userId)}/${encodeURIComponent(frameId)}?IsLensOverrideRequired=${encodeURIComponent(noOverride)}`;

    try {
        const response = await buildRequest(url);

        if (!response.ok) {
            if (response.status === 404) {
                console.warn('No Lens Options found for the given criteria.');
                return [];
            }
            else if (response.status === 401) {
                deleteCookie();
                throw new UnauthorisedError();
            }
            else {
                throw new Error(`Failed to fetch Lens Options: ${response.status} ${response.statusText}`);
            }
        }

        return await response.json();
    } catch (error) {
        throw new Error(error.message);
    }
}

export async function fetchCoatsAndTintsForLens(userId,lensId,noOverrideTints = true, noOverrideCoats = true) {
    let url = `/Optician/Clinic/GetManagedClinicApprovedCoatsTintsAndCases/${encodeURIComponent(userId)}/${encodeURIComponent(lensId)}?lAuthorisedTints=${encodeURIComponent(noOverrideTints)}&lAuthorisedCoats=${encodeURIComponent(noOverrideCoats)}`;

    try {
        const response = await buildRequest(url);

        if (!response.ok) {
            if (response.status === 404) {
                console.warn('No Coats or Tints found for the given criteria.');
                return [];
            }
            else if (response.status === 401) {
                deleteCookie();
                throw new UnauthorisedError();
            }
            else {
                throw new Error(`Failed to fetch Coats and Tints : ${response.status} ${response.statusText}`);
            }
        }

        return await response.json();
    } catch (error) {
        throw new Error(error.message);
    }
}

export async function fetchPrescriptionMeasures(lensId, frameId, singleVisionType) {
    switch (singleVisionType) {
        case "Distance":
            singleVisionType = 0;
            break;
        case "Readers":
            singleVisionType = 1;
            break;
        case "Intermediate":
            singleVisionType = 2;
            break;
        default:
            singleVisionType = 1;
            break;
    }

    let url = `/Optician/Clinic/GetManagedClinicStandardPrescriptionMeasures/${encodeURIComponent(lensId)}/${encodeURIComponent(frameId)}/${encodeURIComponent(singleVisionType)}`;

    try {
        const response = await buildRequest(url);

        if (!response.ok) {
            if (response.status === 404) {
                console.warn('No Prescription Measures found for the given criteria.');
                return [];
            }
            else if (response.status === 401) {
                deleteCookie();
                throw new UnauthorisedError();
            }
            else {
                throw new Error(`Failed to fetch Prescription Measures : ${response.status} ${response.statusText}`);
            }
        }

        return await response.json();
    } catch (error) {
        throw new Error(error.message);
    }
}

export async function fetchLensBrand(userId, frameId, lensType, noOverride = true) {
    let url = `/Optician/Clinic/GetManagedClinicLensBrand/${encodeURIComponent(userId)}/${encodeURIComponent(frameId)}?lLensStandard=${encodeURIComponent(noOverride)}&cSelectedLensType=${encodeURIComponent(lensType)}`;

    if(!noOverride)
        {
            url += "&lEyeTicketOveride=true"
        }

    try {
        const response = await buildRequest(url);

        if (!response.ok) {
            if (response.status === 404) {
                console.warn('No Lens Brand found for the given criteria.');
                return [];
            }
            else if (response.status === 401) {
                deleteCookie();
                throw new UnauthorisedError();
            }
            else {
                throw new Error(`Failed to fetch Lens Brand: ${response.status} ${response.statusText}`);
            }
        }

        return await response.json();
    } catch (error) {
        throw new Error(error.message);
    }
}

export async function processNonEyeTicketRequest(userId, accountCode, eyeTicket) {
    const url = `/Optician/Clinic/ProcessManagedClinicEmployeeVisionRequest/${encodeURIComponent(userId)}?cManagedAccCode=${accountCode}`;

    try {
        const response = await buildRequest(url, 'POST', eyeTicket);
        if (!response.ok) {
            if (response.status === 404 || response.status === 500 || response.status === 400) {
                console.warn('Eyeticket Failed To Process');
                return [];
            }
            else if (response.status === 401) {
                deleteCookie();
                throw new UnauthorisedError();
            }
            else {
                throw new Error(`Eyeticket Failed To Process: ${response.status} ${response.statusText}`);
            }
        }

        return await response.json();
    } catch (error) {
        throw new Error(error.message);
    }
}

export async function processEyeTicket(eyeTicket) {
    const url = `/Optician/Clinic/CompleteManagedClinicCompleteOrder`;

    try {
        const response = await buildRequest(url, 'POST', eyeTicket);
        const text = await response.text();
        try {
            const data = JSON.parse(text);
            if (!response.ok) {
                if (response.status === 401) {
                    deleteCookie();
                    throw new UnauthorisedError();
                }
                else {
                    throw new Error(`Failed to process EyeTicket: ${response.status} ${response.statusText} | ${data.detail || text}`);
                }
            }
            return data;
        } catch (err) {
            if (!response.ok) {
                if (response.status === 401) {
                    deleteCookie();
                    throw new UnauthorisedError();
                }
                else {
                    throw new Error(`Failed to process EyeTicket: ${response.status} ${response.statusText} | ${text}`);
                }
            }
        }
    } catch (error) {
        throw new Error(error.message);
    }
}